import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useDeleteInspection, type Inspection, type InspectionStatus } from '../../api/inspections';
import { useTransition } from '../../api/workflow';
import { useEnumLabel } from '../../api/enums';
import { isApiError } from '../../api/errors';
import { Button, ButtonGroup, ButtonLink, ConfirmDialog, Details, NotificationBanner, RequiredFieldsNote, Select, Textarea } from '../../components/idsk';
import { useNavigateWithToast } from '../../components/common/navigateWithToast';
import { toast } from '../../components/common/toasts';
import { useInspectionForms } from '../../api/forms';
import { ReportAction, ReportConfirmDialog } from './documents/ReportAction';

const NEXT: Partial<Record<InspectionStatus, InspectionStatus>> = { PROPOSAL: 'PLANNING', PLANNING: 'EXECUTION', EXECUTION: 'EVALUATION' };

/**
 * The actions the current user may use (the backend enforces the same rules). ADMIN sets any status (except
 * Ukončená, reached through the report) with a select; every other team member – the leader too (D27) – has "Prejsť do
 * ďalšieho stavu", which moves the inspection to the next status. In Hodnotenie only the leader goes on: "Hotovo"
 * generates the report, which finishes the inspection.
 */
export function InspectionActions({ inspection }: { inspection: Inspection }) {
  const { t } = useTranslation();
  const label = useEnumLabel();
  const p = inspection.permissions;
  const transition = useTransition(inspection.id);
  const remove = useDeleteInspection();
  const navigateWithToast = useNavigateWithToast();
  const [error, setError] = useState<{ text: string; formLink: boolean } | null>(null);
  const [target, setTarget] = useState('');
  const [reason, setReason] = useState('');
  const [targetError, setTargetError] = useState<string | undefined>();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmReport, setConfirmReport] = useState(false);
  const next = NEXT[inspection.status];
  const currentForm = inspection.status === 'FINISHED' ? null : inspection.status;
  // the team's own step: the next status (any member); in Hodnotenie the report (the leader only)
  const evaluation = inspection.status === 'EVALUATION';
  const team = (p.leader || p.member) && !p.admin && currentForm !== null;
  const done = team && (!evaluation || p.leader);
  const forms = useInspectionForms(done && evaluation ? inspection.id : undefined);
  // the server reports the next status as allowed only with a complete form; the evaluation form via its summary
  const currentComplete =
    inspection.status === 'EVALUATION' ? forms.data?.find((f) => f.formType === 'EVALUATION')?.complete === true : Boolean(next && p.allowedTransitions.includes(next));

  const run = async (targetStatus: InspectionStatus, why?: string) => {
    setError(null);
    try {
      const updated = await transition.mutateAsync({ targetStatus, version: inspection.version, reason: why });
      toast.success(t('workflow.changed', { status: label('inspectionStatuses', updated.status) }));
      setTarget('');
      setReason('');
    } catch (e) {
      if (isApiError(e)) setError({ text: e.isVersionConflict ? t('errors.versionConflict') : e.message, formLink: e.code === 'FORM_INCOMPLETE' });
      else setError({ text: t('errors.unexpected'), formLink: false });
    }
  };

  const adminSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!target) {
      setTargetError(t('workflow.errors.target'));
      return;
    }
    setTargetError(undefined);
    void run(target as InspectionStatus, reason);
  };

  const markDone = () => {
    setError(null);
    if (!currentComplete) {
      setError({ text: t('workflow.doneIncomplete', { form: label('formTypes', currentForm) }), formLink: true });
      return;
    }
    if (inspection.status === 'EVALUATION') setConfirmReport(true);
    else void run(next!);
  };

  const nothing = !p.admin && !p.leader && !p.member && !p.canGenerateReport;
  if (nothing && !p.canEdit) return null;

  return (
    <section aria-labelledby="actions-heading" className="govuk-!-margin-bottom-6">
      <h2 className="govuk-heading-m" id="actions-heading">
        {t('workflow.heading')}
      </h2>
      {error && (
        <NotificationBanner type="error" title={t('workflow.failed')}>
          {error.text}{' '}
          {error.formLink && currentForm && (
            <Link className="govuk-link" to={`/inspekcie/${inspection.id}/formulare/${currentForm}`}>
              {t('workflow.openForm')}
            </Link>
          )}
        </NotificationBanner>
      )}
      {inspection.status === 'FINISHED' && (
        <p className="govuk-body">
          {t('workflow.finished')}{' '}
          {inspection.reportAvailable && (
            <Link className="govuk-link" to={`/inspekcie/${inspection.id}/sprava`}>
              {t('report.view.open')}
            </Link>
          )}
        </p>
      )}
      {done && (
        <>
          <p className="govuk-body" id="done-description">
            {inspection.status === 'EVALUATION'
              ? t('workflow.doneReport')
              : t('workflow.doneNext', { status: label('inspectionStatuses', inspection.status), next: label('inspectionStatuses', next) })}
            {(inspection.status !== 'EVALUATION' || forms.data) && !currentComplete && (
              <>
                {' '}
                {t('workflow.doneIncomplete', { form: label('formTypes', currentForm) })}{' '}
                <Link className="govuk-link" to={`/inspekcie/${inspection.id}/formulare/${currentForm}`}>
                  {t('workflow.openForm')}
                </Link>
              </>
            )}
          </p>
          <Button onClick={markDone} aria-describedby="done-description" preventDoubleClick>
            {evaluation ? t('workflow.done') : t('workflow.proceed')}
          </Button>
          <ReportConfirmDialog inspection={inspection} open={confirmReport} onClose={() => setConfirmReport(false)} onError={(text) => setError({ text, formLink: false })} />
        </>
      )}
      {team && !done && <p className="govuk-body">{t('workflow.reportByLeader')}</p>}
      {p.admin && p.allowedTransitions.length > 0 && (
        <Details summary={t('workflow.adminSummary')}>
          <form onSubmit={adminSubmit} noValidate>
            <RequiredFieldsNote />
            <Select
              id="transition-target"
              label={t('workflow.target')}
              required
              hint={t('workflow.targetHint')}
              placeholder={t('common.select')}
              options={p.allowedTransitions.map((s) => ({ value: s, label: label('inspectionStatuses', s) }))}
              value={target}
              error={targetError}
              onChange={(e) => setTarget(e.target.value)}
            />
            <Textarea id="transition-reason" label={t('workflow.reason')} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
            <Button type="submit">{t('workflow.change')}</Button>
          </form>
        </Details>
      )}
      {p.admin && p.canGenerateReport && <ReportAction inspection={inspection} onError={(text) => setError({ text, formLink: false })} />}
      {(p.canEdit || p.canDelete) && (
        <ButtonGroup>
          {p.canEdit && (
            <ButtonLink to={`/inspekcie/${inspection.id}/upravit`} variant="secondary">
              {t('workflow.edit')}
            </ButtonLink>
          )}
          {p.canDelete && (
            <Button variant="warning" onClick={() => setConfirmDelete(true)}>
              {t('workflow.delete')}
            </Button>
          )}
        </ButtonGroup>
      )}
      <ConfirmDialog
        open={confirmDelete}
        title={t('workflow.confirmDelete.title')}
        confirmText={t('workflow.confirmDelete.confirm')}
        danger
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => {
          setConfirmDelete(false);
          void remove
            .mutateAsync({ id: inspection.id, version: inspection.version })
            .then(() => navigateWithToast('/inspekcie', t('workflow.deleted', { number: inspection.number })))
            .catch((e: unknown) => setError({ text: isApiError(e) ? e.message : t('errors.unexpected'), formLink: false }));
        }}
      >
        <p className="govuk-body">{t('workflow.confirmDelete.body', { number: inspection.number })}</p>
      </ConfirmDialog>
    </section>
  );
}
